import React, { useCallback, useEffect, useState } from "react";
import {
    Box, Paper, Typography, Stack, Chip, LinearProgress,
    IconButton, Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import RefreshRoundedIcon         from "@mui/icons-material/RefreshRounded";
import ExpandMoreRoundedIcon      from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon      from "@mui/icons-material/ExpandLessRounded";
import AttachFileRoundedIcon      from "@mui/icons-material/AttachFileRounded";
import { mailApi }                from "../../api/mailApi";
import { formatFileSize as formatBytes } from "../../../../util/numberFormat";

/** 바이트 → 사람이 읽는 크기. 첨부 칩 라벨용이라 소수점 1자리면 충분. */

const STATUS_META = {
    SCHEDULED: { label: "예약 대기", color: "#1d4ed8", bg: "#dbeafe" },
    QUEUED:  { label: "대기",   color: "#64748b", bg: "#f1f5f9" },
    SENDING: { label: "발송중", color: "#b45309", bg: "#fef3c7" },
    SENT:    { label: "성공",   color: "#15803d", bg: "#dcfce7" },
    FAILED:  { label: "실패",   color: "#b91c1c", bg: "#fee2e2" },
    DONE:    { label: "완료",   color: "#15803d", bg: "#dcfce7" },
};

function StatusChip({ status }) {
    const m = STATUS_META[status] || STATUS_META.QUEUED;
    return <Chip label={m.label} size="small"
                 sx={{ height: 20, fontSize: 11, fontWeight: 700, color: m.color, bgcolor: m.bg }} />;
}

/**
 * 단체(개인별 개별) 발송 내역 — 예약함의 '단체 발송' 탭에서 사용.
 * 작성은 메일 쓰기(MailWriteModal)의 '단체' 토글에서 하고, 진행/결과는 여기서 확인한다.
 */
export default function BulkMailPanel() {
    const [batches, setBatches]   = useState([]);
    const [expanded, setExpanded] = useState(null);   // 열린 배치 id
    const [detail, setDetail]     = useState(null);    // 열린 배치의 상세(수신자 목록)

    const loadBatches = useCallback(async () => {
        try {
            const res = await mailApi.listBulk(0, 50);
            const data = res?.data ?? res ?? {};
            setBatches(Array.isArray(data) ? data : (data.content || []));
        } catch { /* noop */ }
    }, []);

    useEffect(() => { loadBatches(); }, [loadBatches]);

    // 진행 중 배치가 있으면 5초마다 폴링. 단, 15분 넘게 남은 '예약 대기' 배치는 곧 변화가 없으므로
    // 폴링 대상에서 제외(먼 미래 예약이 창을 열어둔 내내 5초 폴링하는 낭비 방지). 임박한 예약은 그대로 폴링.
    const anyRunning = batches.some(b => {
        if (b.status === "DONE") return false;
        if (b.scheduledAt && (b.sentCount || 0) + (b.failedCount || 0) === 0
            && new Date(b.scheduledAt).getTime() - Date.now() > 15 * 60 * 1000) return false;
        return true;
    });
    useEffect(() => {
        if (!anyRunning) return;
        const t = setInterval(loadBatches, 5000);
        return () => clearInterval(t);
    }, [anyRunning, loadBatches]);

    const toggleExpand = async (batchId) => {
        if (expanded === batchId) { setExpanded(null); setDetail(null); return; }
        setExpanded(batchId); setDetail(null);
        try {
            const res = await mailApi.getBulk(batchId);
            setDetail(res?.data ?? res ?? null);
        } catch { setDetail(null); }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 13, color: "#64748b", flex: 1 }}>
                    수신자마다 개별 메일 1통씩 발송된 배치별 진행 상황입니다. (작성은 <b>메일 쓰기 → 단체</b>)
                </Typography>
                <IconButton size="small" onClick={loadBatches}><RefreshRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
            </Stack>

            <Paper variant="outlined" sx={{ borderColor: "#e2e8f0", borderRadius: 2, overflow: "hidden" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ "& th": { fontSize: 12, fontWeight: 700, color: "#64748b", bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" } }}>
                            <TableCell>제목</TableCell>
                            <TableCell align="center" sx={{ width: 80 }}>상태</TableCell>
                            <TableCell align="center" sx={{ width: 150 }}>성공 / 실패 / 총</TableCell>
                            <TableCell align="center" sx={{ width: 200 }}>진행</TableCell>
                            <TableCell align="center" sx={{ width: 120 }}>등록 시각</TableCell>
                            <TableCell align="center" sx={{ width: 48 }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {batches.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: 13 }}>
                                    아직 단체 발송 내역이 없습니다. <b>메일 쓰기 → 단체</b>로 첫 단체 메일을 보내보세요.
                                </TableCell>
                            </TableRow>
                        ) : batches.map((b) => {
                            const done = (b.sentCount || 0) + (b.failedCount || 0);
                            const pct = b.totalCount ? Math.round((done / b.totalCount) * 100) : 0;
                            const open = expanded === b.id;
                            // 예약 대기 = 예약 시각이 아직 미래이고 아무도 발송 전.
                            const schedWaiting = b.scheduledAt && new Date(b.scheduledAt).getTime() > Date.now() && done === 0;
                            const effStatus = schedWaiting ? "SCHEDULED" : b.status;
                            const schedText = b.scheduledAt ? String(b.scheduledAt).slice(5, 16).replace("T", " ") : null;
                            return (
                                <React.Fragment key={b.id}>
                                    <TableRow hover>
                                        <TableCell sx={{ maxWidth: 300 }}>

                                            {schedText && (
                                                <Typography noWrap sx={{ fontSize: 11, color: schedWaiting ? "#1d4ed8" : "#94a3b8", mt: 0.2 }}>
                                                    {schedText} 예약
                                                </Typography>
                                            )}
                                            <Stack direction="row" alignItems="center" spacing={0.6} sx={{ minWidth: 0 }}>
                                                <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", minWidth: 0 }}>
                                                    {b.subject || "(제목 없음)"}
                                                </Typography>
                                                {/* 첨부는 배치당 1벌만 저장되고 수신자 전원의 개별 메일에 동일하게 붙는다.
                                                    파일명·크기는 행을 펼치면 아래 상세에서 볼 수 있다. */}
                                                {b.attachmentCount > 0 && (
                                                    <Stack direction="row" alignItems="center" spacing={0.2}
                                                           title={`첨부 ${b.attachmentCount}개 — 수신자 모두에게 동일하게 전달`}
                                                           sx={{ flexShrink: 0, color: "#64748b" }}>
                                                        <AttachFileRoundedIcon sx={{ fontSize: 13 }} />
                                                        <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{b.attachmentCount}</Typography>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="center"><StatusChip status={effStatus} /></TableCell>
                                        <TableCell align="center" sx={{ fontSize: 12.5 }}>
                                            <span style={{ color: "#15803d", fontWeight: 700 }}>{b.sentCount || 0}</span>
                                            {" / "}<span style={{ color: "#b91c1c", fontWeight: 700 }}>{b.failedCount || 0}</span>
                                            {" / "}<span style={{ color: "#64748b" }}>{b.totalCount}</span>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <LinearProgress variant="determinate" value={pct}
                                                    sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                                                <Typography sx={{ fontSize: 11, color: "#94a3b8", minWidth: 32, textAlign: "right" }}>{pct}%</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: 11.5, color: "#94a3b8", whiteSpace: "nowrap" }}>
                                            {b.createdAt ? String(b.createdAt).slice(5, 16).replace("T", " ") : "-"}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => toggleExpand(b.id)}>
                                                {open ? <ExpandLessRoundedIcon sx={{ fontSize: 18 }} /> : <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    {open && (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ bgcolor: "#f8fafc", py: 1.2 }}>
                                                {!detail ? (
                                                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>불러오는 중...</Typography>
                                                ) : (
                                                    <Stack spacing={0.4}>
                                                        {/* 첨부 — 수신자 목록 위에 한 번만. 배치 공용이라 수신자별로 반복하지 않는다. */}
                                                        {(detail.attachments || []).length > 0 && (
                                                            <Stack direction="row" alignItems="center" flexWrap="wrap"
                                                                   sx={{ gap: 0.6, pb: 0.8, mb: 0.4, borderBottom: "1px solid #e2e8f0" }}>
                                                                <Typography sx={{ fontSize: 11.5, color: "#64748b", fontWeight: 700, mr: 0.4 }}>
                                                                    첨부 {detail.attachments.length}개
                                                                </Typography>
                                                                {detail.attachments.map((a) => (
                                                                    <Chip
                                                                        key={a.id}
                                                                        size="small"
                                                                        icon={<AttachFileRoundedIcon sx={{ fontSize: 13 }} />}
                                                                        label={`${a.filename}${a.sizeBytes ? ` (${formatBytes(a.sizeBytes)})` : ""}`}
                                                                        sx={{ height: 20, fontSize: 11, bgcolor: "#fff",
                                                                              border: "1px solid #e2e8f0", color: "#475569" }}
                                                                    />
                                                                ))}
                                                            </Stack>
                                                        )}
                                                        {(detail.recipients || []).map((r) => (
                                                            <Stack key={r.id} direction="row" alignItems="center" spacing={1}>
                                                                <Typography noWrap sx={{ fontSize: 12.5, color: "#334155", flex: 1, minWidth: 0 }}>
                                                                    {r.displayName ? `${r.displayName} <${r.address}>` : r.address}
                                                                </Typography>
                                                                {r.failureReason && (
                                                                    <Typography noWrap title={r.failureReason}
                                                                        sx={{ fontSize: 11, color: "#b91c1c", maxWidth: 320 }}>
                                                                        {r.failureReason}
                                                                    </Typography>
                                                                )}
                                                                <StatusChip status={r.status} />
                                                            </Stack>
                                                        ))}
                                                    </Stack>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}
